<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue';

const props = defineProps({
  videoId: {
    type: String,
    required: true,
  },
});

const containerRef = ref(null);
const playerRef = ref(null);
const isPlaying = ref(false);
const currentTime = ref(0);
const duration = ref(0);
const playbackRate = ref(1);
const timerRef = ref(null);

let destroyed = false;

const RATES = [0.75, 1, 1.25, 1.5, 1.75, 2];

const progressPercent = computed(() =>
  duration.value > 0 ? (currentTime.value / duration.value) * 100 : 0
);

const formatTime = seconds => {
  const s = Math.floor(seconds || 0);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

const loadYouTubeAPI = () => {
  return new Promise(resolve => {
    if (window.YT?.Player) {
      resolve();
      return;
    }
    const prev = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      if (prev) prev();
      resolve();
    };
    if (!document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const s = document.createElement('script');
      s.src = 'https://www.youtube.com/iframe_api';
      document.head.appendChild(s);
    }
  });
};

const startPolling = () => {
  timerRef.value = setInterval(() => {
    if (playerRef.value?.getCurrentTime) {
      currentTime.value = playerRef.value.getCurrentTime() ?? 0;
    }
  }, 200);
};

const stopPolling = () => {
  clearInterval(timerRef.value);
  timerRef.value = null;
};

const togglePlay = () => {
  if (!playerRef.value) return;
  if (isPlaying.value) {
    playerRef.value.pauseVideo();
  } else {
    playerRef.value.playVideo();
  }
};

const seek = time => {
  playerRef.value?.seekTo(Number(time), true);
  currentTime.value = Number(time);
};

const setRate = rate => {
  playerRef.value?.setPlaybackRate(rate);
  playbackRate.value = rate;
};

onMounted(async () => {
  await loadYouTubeAPI();
  if (destroyed || !containerRef.value) return;

  playerRef.value = new window.YT.Player(containerRef.value, {
    videoId: props.videoId,
    playerVars: {
      controls: 0,
      modestbranding: 1,
      rel: 0,
      showinfo: 0,
      iv_load_policy: 3,
      fs: 0,
      playsinline: 1,
      enablejsapi: 1,
      origin: window.location.origin,
    },
    events: {
      onReady: e => {
        if (!destroyed) {
          duration.value = e.target.getDuration();
        }
      },
      onStateChange: e => {
        if (destroyed) return;
        if (e.data === 1) {
          // PLAYING
          isPlaying.value = true;
          startPolling();
        } else if (e.data === 2) {
          // PAUSED
          isPlaying.value = false;
          stopPolling();
        } else if (e.data === 0) {
          // ENDED
          isPlaying.value = false;
          stopPolling();
          currentTime.value = 0;
        }
      },
    },
  });
});

onUnmounted(() => {
  destroyed = true;
  stopPolling();
  playerRef.value?.destroy();
  playerRef.value = null;
});
</script>

<template>
  <div
    class="relative w-full bg-black rounded-xl overflow-hidden"
    style="aspect-ratio: 16/9"
  >
    <!-- YouTube player container — replaced by iframe via IFrame API -->
    <div ref="containerRef" class="absolute inset-0 w-full h-full" />

    <!-- Overlay: blocks YouTube logo/branding clicks, enables click-to-play -->
    <div
      class="absolute inset-0 cursor-pointer"
      style="z-index: 1"
      @click="togglePlay"
    />

    <!-- Controls bar -->
    <div
      class="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent px-4 pb-3 pt-10"
      style="z-index: 2"
    >
      <!-- Progress bar -->
      <div class="relative h-1 mb-3">
        <div class="absolute inset-0 bg-white/30 rounded-full" />
        <div
          class="absolute inset-y-0 left-0 bg-red-500 rounded-full"
          :style="{ width: `${progressPercent}%` }"
        />
        <input
          type="range"
          min="0"
          :max="duration"
          :value="currentTime"
          class="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          @input="seek($event.target.value)"
        />
      </div>

      <!-- Controls row -->
      <div class="flex items-center gap-3">
        <!-- Play/Pause -->
        <button
          class="text-white hover:text-white/80 transition-colors"
          @click="togglePlay"
        >
          <i
            class="w-5 h-5"
            :class="isPlaying ? 'i-lucide-pause' : 'i-lucide-play'"
          />
        </button>

        <!-- Time display -->
        <span class="text-white text-xs font-mono tabular-nums">
          {{ formatTime(currentTime) }} / {{ formatTime(duration) }}
        </span>

        <div class="flex-1" />

        <!-- Playback speed -->
        <div class="flex items-center gap-0.5">
          <button
            v-for="rate in RATES"
            :key="rate"
            class="text-xs px-1.5 py-0.5 rounded transition-colors"
            :class="
              playbackRate === rate
                ? 'bg-white text-black font-semibold'
                : 'text-white/60 hover:text-white'
            "
            @click="setRate(rate)"
          >
            {{ rate }}×
          </button>
        </div>
      </div>
    </div>
  </div>
</template>
